import { supabase } from "@/integrations/supabase/client";
import { Event } from "@/types/event";

type EventRow = {
  id: string;
  slug: string;
  title_en: string;
  title_fr: string;
  short_description_en: string;
  short_description_fr: string;
  description_en: string;
  description_fr: string;
  event_date: string;
  cover_image: string | null;
  images: string[] | null;
  is_published: boolean | null;
  created_at: string;
  updated_at: string;
};

type EventsPageSettingsRow = {
  id: string;
  hero_eyebrow_en: string | null;
  hero_eyebrow_fr: string | null;
  hero_title_en: string | null;
  hero_title_fr: string | null;
  hero_subtitle_en: string | null;
  hero_subtitle_fr: string | null;
  hero_background_image: string | null;
};

export type EventInput = {
  slug: string;
  date: string;
  coverImage?: string;
  titleEn: string;
  titleFr: string;
  shortDescriptionEn: string;
  shortDescriptionFr: string;
  descriptionEn: string;
  descriptionFr: string;
  images: string[];
  isPublished: boolean;
};

export type EventsPageSettings = {
  heroEyebrowEn: string;
  heroEyebrowFr: string;
  heroTitleEn: string;
  heroTitleFr: string;
  heroSubtitleEn: string;
  heroSubtitleFr: string;
  heroBackgroundImage: string;
};

const mapEventRowToEvent = (row: EventRow): Event => {
  const normalizedImages = row.images ?? [];
  const cover = row.cover_image || normalizedImages[0] || "";
  const gallery = normalizedImages.length > 0 ? normalizedImages : cover ? [cover] : [];

  return {
    id: row.id,
    slug: row.slug,
    date: row.event_date,
    coverImage: cover,
    title: { en: row.title_en, fr: row.title_fr },
    shortDescription: {
      en: row.short_description_en,
      fr: row.short_description_fr,
    },
    description: { en: row.description_en, fr: row.description_fr },
    images: gallery.map((url, index) => ({
      url,
      alt: {
        en: `${row.title_en} image ${index + 1}`,
        fr: `${row.title_fr} image ${index + 1}`,
      },
    })),
    isPublished: Boolean(row.is_published),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

const mapInputToPayload = (input: EventInput) => ({
  slug: input.slug,
  event_date: input.date,
  cover_image: input.coverImage || input.images[0] || null,
  title_en: input.titleEn,
  title_fr: input.titleFr,
  short_description_en: input.shortDescriptionEn,
  short_description_fr: input.shortDescriptionFr,
  description_en: input.descriptionEn,
  description_fr: input.descriptionFr,
  images: input.images,
  is_published: input.isPublished,
});

const defaultEventsPageSettings: EventsPageSettings = {
  heroEyebrowEn: "Experience Meubles Karim",
  heroEyebrowFr: "Experience Meubles Karim",
  heroTitleEn: "Our Signature Events",
  heroTitleFr: "Nos Evenements Signature",
  heroSubtitleEn: "Discover our standout moments: launches, private showcases, and immersive design experiences.",
  heroSubtitleFr: "Decouvrez nos moments d'exception: lancements, showcases prives et experiences immersives.",
  heroBackgroundImage: "",
};

const mapSettingsRowToSettings = (
  row: EventsPageSettingsRow | null | undefined,
): EventsPageSettings => {
  if (!row) return defaultEventsPageSettings;

  return {
    heroEyebrowEn: row.hero_eyebrow_en || defaultEventsPageSettings.heroEyebrowEn,
    heroEyebrowFr: row.hero_eyebrow_fr || defaultEventsPageSettings.heroEyebrowFr,
    heroTitleEn: row.hero_title_en || defaultEventsPageSettings.heroTitleEn,
    heroTitleFr: row.hero_title_fr || defaultEventsPageSettings.heroTitleFr,
    heroSubtitleEn: row.hero_subtitle_en || defaultEventsPageSettings.heroSubtitleEn,
    heroSubtitleFr: row.hero_subtitle_fr || defaultEventsPageSettings.heroSubtitleFr,
    heroBackgroundImage: row.hero_background_image || "",
  };
};

export const fetchPublishedEvents = async (): Promise<Event[]> => {
  const { data, error } = await supabase
    .from("events")
    .select("*")
    .eq("is_published", true)
    .order("event_date", { ascending: false });

  if (error) throw error;
  return ((data as EventRow[] | null) ?? []).map(mapEventRowToEvent);
};

export const fetchPublishedEventBySlug = async (slug: string): Promise<Event | null> => {
  const { data, error } = await supabase
    .from("events")
    .select("*")
    .eq("slug", slug)
    .eq("is_published", true)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return mapEventRowToEvent(data as EventRow);
};

export const fetchAdminEvents = async (): Promise<Event[]> => {
  const { data, error } = await supabase
    .from("events")
    .select("*")
    .order("event_date", { ascending: false });

  if (error) throw error;
  return ((data as EventRow[] | null) ?? []).map(mapEventRowToEvent);
};

export const fetchAdminEventById = async (id: string): Promise<Event | null> => {
  const { data, error } = await supabase
    .from("events")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return mapEventRowToEvent(data as EventRow);
};

export const createEvent = async (input: EventInput): Promise<Event> => {
  const { data, error } = await supabase
    .from("events")
    .insert([mapInputToPayload(input)])
    .select("*")
    .single();

  if (error) throw error;
  return mapEventRowToEvent(data as EventRow);
};

export const updateEvent = async (id: string, input: EventInput): Promise<Event> => {
  const { data, error } = await supabase
    .from("events")
    .update(mapInputToPayload(input))
    .eq("id", id)
    .select("*")
    .single();

  if (error) throw error;
  return mapEventRowToEvent(data as EventRow);
};

export const deleteEvent = async (id: string): Promise<void> => {
  const { error } = await supabase.from("events").delete().eq("id", id);
  if (error) throw error;
};

export const fetchEventsPageSettings = async (): Promise<EventsPageSettings> => {
  const { data, error } = await supabase
    .from("events_page_settings")
    .select("*")
    .eq("id", "events-page")
    .maybeSingle();

  if (error) throw error;
  return mapSettingsRowToSettings(data as EventsPageSettingsRow | null);
};

export const upsertEventsPageSettings = async (
  input: EventsPageSettings,
): Promise<EventsPageSettings> => {
  const { data, error } = await supabase
    .from("events_page_settings")
    .upsert(
      [
        {
          id: "events-page",
          hero_eyebrow_en: input.heroEyebrowEn,
          hero_eyebrow_fr: input.heroEyebrowFr,
          hero_title_en: input.heroTitleEn,
          hero_title_fr: input.heroTitleFr,
          hero_subtitle_en: input.heroSubtitleEn,
          hero_subtitle_fr: input.heroSubtitleFr,
          hero_background_image: input.heroBackgroundImage || null,
        },
      ],
      { onConflict: "id" },
    )
    .select("*")
    .single();

  if (error) throw error;
  return mapSettingsRowToSettings(data as EventsPageSettingsRow);
};
